import { useState } from "react";
import { api, setToken, formatApiErrorDetail } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { KeyRound, Loader2 } from "lucide-react";
import { toast } from "sonner";

export function ChangePasswordDialog({ open, onOpenChange }) {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  function reset() { setCurrent(""); setNext(""); setConfirm(""); setErr(""); }

  async function submit(e) {
    e.preventDefault();
    setErr("");
    if (next.length < 8) { setErr("New password must be at least 8 characters."); return; }
    if (next !== confirm) { setErr("New password and confirmation do not match."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/auth/change-password", {
        current_password: current, new_password: next,
      });
      if (data?.access_token) setToken(data.access_token);
      toast.success("Password changed. All other sessions have been signed out.");
      reset();
      onOpenChange(false);
    } catch (ex) {
      setErr(formatApiErrorDetail(ex?.response?.data?.detail) || "Failed to change password.");
    } finally {
      setBusy(false);
    }
  }

  const inputCls = "w-full border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:border-[#F97316]";

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!busy) { reset(); onOpenChange(v); } }}>
      <DialogContent className="max-w-sm" data-testid="change-password-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <KeyRound size={16} className="text-[#F97316]" /> Change My Password
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          {err && (
            <div data-testid="cpd-error" className="text-sm text-red-700 border border-red-200 bg-red-50 px-3 py-2">{err}</div>
          )}
          <div>
            <div className="text-[10px] uppercase tracking-wide text-slate-500 font-bold mb-1">Current Password</div>
            <input type="password" data-testid="cpd-current" className={inputCls} autoComplete="current-password"
                   value={current} onChange={(e) => setCurrent(e.target.value)} required />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wide text-slate-500 font-bold mb-1">New Password (min 8 characters)</div>
            <input type="password" data-testid="cpd-new" className={inputCls} autoComplete="new-password"
                   value={next} onChange={(e) => setNext(e.target.value)} required />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wide text-slate-500 font-bold mb-1">Confirm New Password</div>
            <input type="password" data-testid="cpd-confirm" className={inputCls} autoComplete="new-password"
                   value={confirm} onChange={(e) => setConfirm(e.target.value)} required />
          </div>
          <p className="text-[11px] text-slate-500">
            You'll stay signed in on this device. Every other session will be signed out.
          </p>
          <button type="submit" disabled={busy} data-testid="cpd-submit"
                  className="w-full bg-[#F97316] hover:bg-[#EA580C] disabled:opacity-60 text-white font-bold uppercase tracking-wider text-xs py-2.5 transition-colors flex items-center justify-center gap-2">
            {busy && <Loader2 size={13} className="animate-spin" />} Change Password
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
